import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { schema } from "@app/schema";
import type { LinkResponse } from "@app/types";

// Response payload from Deploy API for external AWS resources
export interface DeployExternalAwsResourceResponse {
  id: number;
  external_aws_account_id: string;
  resource_type: string;
  resource_arn: string;
  resource_id: string;
  resource_name: string;
  region: string;
  metadata: Record<string, any> | null;
  tags: Record<string, string> | null;
  discovered_at: string | null;
  last_synced_at: string | null;
  sync_status: string;
  created_at: string;
  updated_at: string;
  _links: {
    self: LinkResponse;
    external_aws_account?: LinkResponse;
    external_aws_database_credentials?: LinkResponse;
  };
  _type: "external_aws_resource";
}

// Domain model (local to this module until global types are added)
export interface DeployExternalAwsResource {
  id: string;
  externalAwsAccountId: string;
  resourceType: string;
  resourceArn: string;
  resourceId: string;
  resourceName: string;
  region: string;
  metadata: Record<string, any>;
  tags: Record<string, string>;
  discoveredAt: string;
  lastSyncedAt: string;
  syncStatus: string;
  createdAt: string;
  updatedAt: string;
}

export const defaultExternalAwsResourceResponse = (
  e: Partial<DeployExternalAwsResourceResponse> = {},
): DeployExternalAwsResourceResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    external_aws_account_id: "",
    resource_type: "",
    resource_arn: "",
    resource_id: "",
    resource_name: "",
    region: "",
    metadata: {},
    tags: {},
    discovered_at: null,
    last_synced_at: null,
    sync_status: "unknown",
    created_at: now,
    updated_at: now,
    _links: {
      self: defaultHalHref(),
      external_aws_account: defaultHalHref(),
      external_aws_database_credentials: defaultHalHref(),
      ...(e._links || {}),
    },
    _type: "external_aws_resource",
    ...e,
  };
};

export const deserializeExternalAwsResource = (
  payload: DeployExternalAwsResourceResponse,
): DeployExternalAwsResource => {
  return {
    id: `${payload.id}`,
    externalAwsAccountId: extractIdFromLink(
      payload._links.external_aws_account,
    ),
    resourceType: payload.resource_type,
    resourceArn: payload.resource_arn,
    resourceId: payload.resource_id,
    resourceName: payload.resource_name,
    region: payload.region,
    metadata: payload.metadata || {},
    tags: payload.tags || {},
    discoveredAt: payload.discovered_at || "",
    lastSyncedAt: payload.last_synced_at || "",
    syncStatus: payload.sync_status,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

// API endpoints
export const fetchExternalAwsResourceById = api.get<{ id: string }>(
  "/external_aws_resources/:id",
);

export const fetchExternalAwsResources = api.get("/external_aws_resources");

// Selectors
export const hasExternalAwsResource = (a: DeployExternalAwsResource) =>
  a.id !== "";
export const selectExternalAwsResourceById =
  schema.externalAwsResources.selectById;
export const selectExternalAwsResourcesAsList =
  schema.externalAwsResources.selectTableAsList;

export const selectExternalAwsResourceCountByAccountId = createSelector(
  selectExternalAwsResourcesAsList,
  (resources) => {
    return resources.reduce<Record<string, number>>((acc, r) => {
      const key = r.externalAwsAccountId;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  },
);

export const selectExternalAwsResourcesByAccountId = createSelector(
  selectExternalAwsResourcesAsList,
  (_: any, props: { accountId: string }) => props.accountId,
  (resources, accountId) =>
    resources.filter((r) => r.externalAwsAccountId === accountId),
);

// Entity registration for HAL parser
export const externalAwsResourceEntities = {
  external_aws_resource: defaultEntity({
    id: "external_aws_resource",
    deserialize: deserializeExternalAwsResource,
    save: schema.externalAwsResources.add,
  }),
};

interface UpdateExternalAwsResource {
  id: string;
  resourceName: string;
}

export const updateExternalAwsResource = api.put<UpdateExternalAwsResource>(
  "/external_aws_resources/:id",
  function* (ctx, next) {
    const { resourceName } = ctx.payload;
    const body = {
      resource_name: resourceName,
    };
    ctx.request = ctx.req({ body: JSON.stringify(body) });

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    ctx.loader = {
      message: "Saved changes successfully!",
    };
  },
);

// Utilities
export {
  getEncryptionAlgorithm,
  getInstanceSpecs,
  getStorageIops,
} from "./utils";
